import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { ItemGroupRecord } from '../types/inventoryItem';

export type ItemGroupFormState = {
  name: string;
  number: string;
  parentId: string | undefined;
  description: string;
  notes: string;
  defaultPricingUnit: string | undefined;
};

const initialState = (): ItemGroupFormState => ({
  name: '',
  number: '',
  parentId: undefined,
  description: '',
  notes: '',
  defaultPricingUnit: 'Pounds',
});

const mapRecordToForm = (record: ItemGroupRecord): ItemGroupFormState => ({
  name: record.name,
  number: record.number,
  parentId: record.parent?.id ?? undefined,
  description: record.description ?? '',
  notes: record.notes ?? '',
  defaultPricingUnit: record.defaultPricingUnit ?? 'Pounds',
});

// 🔘🔘🔘 PLAIN ATOMS
export const $itemGroupForm = atom<ItemGroupFormState>(initialState());
export const $itemGroupDiscardBaseline = atom<ItemGroupFormState | null>(null);
export const $lastPopulatedItemGroupId = atom<string | undefined>(undefined);
export const $isItemGroupFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA
export const $hasItemGroupUnsavedChanges = atom((get) => {
  const form = get($itemGroupForm);
  const baseline = get($itemGroupDiscardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitItemGroupForm = atom((get) => {
  const form = get($itemGroupForm);
  if (!form.name.trim()) return false;
  if (!form.number.trim()) return false;
  return true;
});

// 🟠🟠🟠 ACTION -> UPDATE item group form
export const $updateItemGroupForm = atom(
  null,
  (get, set, update: Partial<ItemGroupFormState>) => {
    set($itemGroupForm, { ...get($itemGroupForm), ...update });
  },
);

export const $populateItemGroupFormFromRecord = atom(
  null,
  (_get, set, record: ItemGroupRecord) => {
    const formState = mapRecordToForm(record);
    set($itemGroupForm, formState);
    set($itemGroupDiscardBaseline, formState);
    set($lastPopulatedItemGroupId, record.id);
    set($isItemGroupFormPopulated, true);
  },
);

export const $resetItemGroupFormToBaseline = atom(null, (get, set) => {
  const baseline = get($itemGroupDiscardBaseline);
  if (baseline) {
    set($itemGroupForm, baseline);
  }
});

export const $populateItemGroupFormAction = atom(
  null,
  (get, set, record: ItemGroupRecord | null | undefined) => {
    if (!record) return;

    const currentRecordId = record.id;
    const lastRecordId = get($lastPopulatedItemGroupId);

    if (currentRecordId !== lastRecordId) {
      set($isItemGroupFormPopulated, false);
    }

    if (!get($isItemGroupFormPopulated)) {
      set($populateItemGroupFormFromRecord, record);
    }
  },
);

export const $resetItemGroupForm = atom(null, (_get, set) => {
  set($itemGroupForm, initialState());
  set($itemGroupDiscardBaseline, null);
  set($lastPopulatedItemGroupId, undefined);
  set($isItemGroupFormPopulated, false);
});
